import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs';

import { SearchRequestModel } from '~app/models/requests/search-request.model';
import { getFilterOperator } from '~components/search-filter/filter/filter-operators';
import { BaseSearchStore } from '~core/abstractions/base-search.store';
import { Identifyable } from '~core/abstractions/identifyable';
import { ColumnFilterModel } from '~core/models/column-filter.model';
import { ComparisonOperatorsEnum, ComparisonOperatorsEnumExtensions } from '~core/models/enums/comparison-operators.enum';
import { FilterItemTypeEnum } from '~core/models/enums/filter-item-type.enum';
import { OperandTypeEnum } from '~core/models/enums/operand-type.enum';
import { FilterItemModel } from '~core/models/filter-item.model';
import { FilterStateModel } from '~core/models/filter-state.model';
import { Utils } from '~core/utils/tools/utils.tools';
import { DictionaryType } from '~shared/core/types/dictionary.type';
import { OptionalType } from '~shared/core/types/optional.type';
import { SavedFiltersService } from './saved-filters.service';
import { SearchRequestService } from './search-request.service';

@Injectable({
    providedIn: 'root'
})
export class FilterService<TId = number> {
    static readonly DEFAULT_FILTER_NAME = 'DEFAULT';

    readonly currentFilters = signal<ColumnFilterModel[]>([], { equal: Utils.areEqual });
    readonly currentReadonlyFilters = signal<ColumnFilterModel[]>([], { equal: Utils.areEqual });

    readonly filterItems = signal<FilterItemModel[]>([]);

    readonly currentState = signal<FilterStateModel>({
        isLoading: false,
        isSaving: false,
        isDeleting: false
    });

    readonly isValid = computed(() => [...this.currentFilters(), ...this.currentReadonlyFilters()].every(f => this.isFilterValid(f)));

    readonly hasUnappliedChanges = computed(
        () => !Utils.areEqual(this.applyableFilters(), this.searchRequestService.currentProperties.filters(), ['internalId'])
    );

    readonly canSave = computed(() => this.isValid() && !this.currentFilters().some(f => this.isFilterEmpty(f)));
    readonly hasUnsavedChanges = computed(
        () =>
            !Utils.areEqual(this.applyableFilters(), this.currentSavedFilter()?.filters, ['internalId'])
            || !Utils.areEqual(this.searchRequestService.currentProperties.sortings(), this.currentSavedFilter()?.sortings)
    );

    readonly savedFilters = computed(() => {
        const allSavedFilters = this.savedFiltersService.currentSavedFilters();
        const baseUrl = this.store()?.getBaseUrl();
        return !!baseUrl ? allSavedFilters[baseUrl] || [] : [];
    });
    readonly currentSavedFilter = signal<OptionalType<SearchRequestModel>>(undefined);

    private store = signal<OptionalType<BaseSearchStore<Identifyable<TId>, TId>>>(undefined);

    private searchResultCountSubInternal = new Subject<SearchRequestModel>();

    private readonly applyableFilters = computed(() => {
        const notEmptyFilters = this.currentFilters().filter(f => !this.isFilterEmpty(f));
        return [...notEmptyFilters, ...this.currentReadonlyFilters()];
    });

    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly searchRequestService = inject(SearchRequestService);
    private readonly savedFiltersService = inject(SavedFiltersService);

    constructor() {
        this.searchResultCountSubInternal
            .pipe(
                filter(() => !!this.store() && this.hasUnappliedChanges()),
                distinctUntilChanged((prev, curr) => Utils.areEqual(prev, curr)),
                tap(() => this.setState({ isLoading: true, resultCount: undefined })),
                debounceTime(200),
                switchMap(searchRequest =>
                    this.store()!.count({
                        sortings: [],
                        filters: searchRequest.filters,
                        searchText: searchRequest.searchText
                    })
                )
            )
            .subscribe(result => {
                this.setState({ isLoading: false, resultCount: result.value ?? 0 });
            });

        effect(() => {
            // when applyable filters change, trigger count
            const applyableFilters = this.applyableFilters();
            const searchText = this.searchRequestService.currentProperties.searchText();

            if (this.isValid()) {
                untracked(() => {
                    this.searchResultCountSubInternal.next({
                        sortings: [],
                        filters: Utils.deepCopy(applyableFilters),
                        searchText
                    });
                });
            }
        });

        effect(() => {
            const searchRequestFilters = this.searchRequestService.currentProperties.filters();
            const filterItems = this.filterItems();

            // when SearchRequestService filters change from outside, update filters here
            const filters = Utils.deepCopy(searchRequestFilters).map(f => ({
                filterModel: new ColumnFilterModel(f),
                filterItem: filterItems.find(fi => fi.columnName === f.column)
            }));
            const writableFilters = filters
                .filter(({ filterItem }) => !!filterItem && !filterItem.isReadOnly)
                .map(({ filterModel }) => filterModel);
            const readonlyFilters = filters
                .filter(({ filterItem }) => !!filterItem && !!filterItem.isReadOnly)
                .map(({ filterModel }) => filterModel);

            untracked(() => {
                if (writableFilters.length > 0) {
                    this.currentFilters.set(writableFilters);
                } else {
                    this.currentFilters.set([new ColumnFilterModel()]);
                }

                this.currentReadonlyFilters.set(readonlyFilters);
            });
        });

        effect(() => {
            // when saved filter id changes from outside, set saved filter here
            const savedFilterId = this.searchRequestService.currentProperties.id();
            const savedFilter = this.savedFilters().find(s => s.id === savedFilterId);

            untracked(() => {
                if (!!savedFilterId && !!savedFilter) {
                    this.applySavedFilter(savedFilterId);
                } else {
                    this.currentSavedFilter.set(undefined);
                }
            });
        });
    }

    initService(store: BaseSearchStore<Identifyable<TId>, TId>): void {
        this.store.set(store);

        store.getAllowedFilters().subscribe(async result => {
            this.filterItems.set(result.value ?? []);

            const defaultFilter = this.savedFilters().find(f => f.name === FilterService.DEFAULT_FILTER_NAME);
            if (this.searchRequestService.isUrlSyncEnabled) {
                // initialize "simple" filters from URL (e.g. ?Id=1234)
                this.readSimpleQueryParamFilters();
            } else if (!!defaultFilter?.id) {
                this.applySavedFilter(defaultFilter.id);
            }
        });

        // always at least one filter should be displayed
        this.addFilter(new ColumnFilterModel());
    }

    resetService(): void {
        this.store.set(undefined);
        this.filterItems.set([]);
        this.currentFilters.set([]);
        this.currentReadonlyFilters.set([]);
        this.currentSavedFilter.set(undefined);
        this.currentState.set({
            isLoading: false,
            isSaving: false,
            isDeleting: false
        });
    }

    addFilter(filterToAdd: ColumnFilterModel): void {
        const filterCopy = new ColumnFilterModel(Utils.deepCopy(filterToAdd));
        if (this.currentFilters().length === 1 && this.isFilterEmpty(this.currentFilters()[0])) {
            this.currentFilters.set([filterCopy]);
        } else {
            this.currentFilters.set([...this.currentFilters(), filterCopy]);
        }
    }

    updateFilter(internalId: string, updatedFilter: Omit<ColumnFilterModel, 'internalId'>): void {
        const filterIndex = this.currentFilters().findIndex(f => f.internalId === internalId);
        if (filterIndex > -1 && !Utils.areEqual(this.currentFilters()[filterIndex], updatedFilter)) {
            this.currentFilters.update(currentFilters => {
                // copy so signal detects an update
                const currentFiltersCopy = Utils.deepCopy(currentFilters);
                currentFiltersCopy[filterIndex].column = updatedFilter.column;
                currentFiltersCopy[filterIndex].comparisonOperator = updatedFilter.comparisonOperator;
                currentFiltersCopy[filterIndex].operand = updatedFilter.operand;
                currentFiltersCopy[filterIndex].values = Utils.deepCopy(updatedFilter.values);
                return currentFiltersCopy;
            });
        }
    }

    removeFilter(internalId: string): void {
        if (!this.currentFilters().find(f => f.internalId === internalId)) {
            return;
        }

        if (this.currentFilters().length === 1) {
            this.currentFilters.set([new ColumnFilterModel()]);
        } else {
            this.currentFilters.set(this.currentFilters().filter(f => f.internalId !== internalId));
        }
    }

    addReadonlyFilter(filterToAdd: ColumnFilterModel): void {
        this.currentReadonlyFilters.set([...this.currentReadonlyFilters(), new ColumnFilterModel(Utils.deepCopy(filterToAdd))]);
    }

    removeReadonlyFilter(internalId: string): void {
        if (!this.currentReadonlyFilters().find(f => f.internalId === internalId)) {
            return;
        }

        this.currentReadonlyFilters.set(this.currentReadonlyFilters().filter(f => f.internalId !== internalId));
    }

    resetAllFilters(): void {
        this.currentFilters.set([new ColumnFilterModel()]);
        this.currentReadonlyFilters.set([]);

        this.apply();
    }

    apply(): void {
        if (this.isValid()) {
            const currentState = this.currentState();
            let shouldDisableTriggerCount = false;
            if (currentState.resultCount !== undefined && !currentState.isLoading) {
                // re-use result count for search
                this.searchRequestService.totalRecords.set(currentState.resultCount);
                this.setState({ resultCount: undefined });
                shouldDisableTriggerCount = true;
            }
            this.searchRequestService.setSearchRequestPartial(
                {
                    id: this.currentSavedFilter()?.id,
                    filters: this.applyableFilters()
                },
                { shouldDisableTriggerCount }
            );
        }
    }

    saveAndApplyCurrentFilter(name: string, isDefaultFilter: boolean): void {
        const baseUrl = this.store()?.getBaseUrl();
        if (!baseUrl) {
            return;
        }
        this.setState({ isSaving: true });
        const currentSearchRequest = this.searchRequestService.current();
        const filtersToSave = this.currentFilters().filter(f => !this.isFilterEmpty(f));

        const filterName = isDefaultFilter ? FilterService.DEFAULT_FILTER_NAME : name;

        const id = this.savedFiltersService.add(baseUrl, {
            name: filterName,
            filters: Utils.deepCopy(filtersToSave),
            sortings: Utils.deepCopy(currentSearchRequest.sortings)
        });

        this.setState({ isSaving: false });
        if (!!id) {
            this.applySavedFilter(id);
        }
    }

    updateSavedFilter(): void {
        const currentSavedFilter = this.currentSavedFilter();
        const baseUrl = this.store()?.getBaseUrl();
        if (!currentSavedFilter?.id || !baseUrl) {
            return;
        }

        this.setState({ isSaving: true });

        const updated = {
            ...currentSavedFilter,
            sortings: Utils.deepCopy(this.searchRequestService.currentProperties.sortings()),
            filters: Utils.deepCopy(this.currentFilters().filter(f => !this.isFilterEmpty(f)))
        };
        this.currentSavedFilter.set(updated);

        this.savedFiltersService.update(baseUrl, currentSavedFilter.id, {
            name: updated.name,
            sortings: updated.sortings,
            filters: updated.filters
        });

        this.setState({ isSaving: false });
    }

    applySavedFilter(id: string): void {
        const savedFilter = Utils.deepCopy(this.savedFilters().find(s => s.id === id));

        if (!savedFilter) {
            return;
        }

        this.currentSavedFilter.set({
            id: savedFilter.id,
            name: savedFilter.name,
            filters: savedFilter.filters,
            sortings: savedFilter.sortings
        });
        this.currentFilters.set(Utils.deepCopy(savedFilter.filters.map(f => new ColumnFilterModel(f))));

        this.apply();
    }

    deleteSavedFilter(id: string): void {
        const baseUrl = this.store()?.getBaseUrl();
        if (!baseUrl) {
            return;
        }

        this.setState({ isDeleting: true });
        this.savedFiltersService.remove(baseUrl, id);
        this.setState({ isDeleting: false });
        this.currentSavedFilter.set(undefined);
        this.resetAllFilters();
    }

    resetSavedFilter(): void {
        this.currentSavedFilter.set(undefined);
    }

    getFilterByName(name: string): OptionalType<ColumnFilterModel> {
        return this.currentFilters().find(cf => cf.column === name);
    }

    private getFilterItem(filterModel: ColumnFilterModel): OptionalType<FilterItemModel> {
        return this.filterItems().find(f => f.columnName === filterModel.column);
    }

    private setState(state: Partial<FilterStateModel>): void {
        this.currentState.set({ ...this.currentState(), ...state });
    }

    private isFilterValid(filterModel: ColumnFilterModel): boolean {
        // empty filters are valid, but won't be included in the final search request
        if (this.isFilterEmpty(filterModel)) {
            return true;
        }

        // filter must have column and comparison operator
        if (!filterModel.column || filterModel.comparisonOperator === undefined) {
            return false;
        }

        const filterItem = this.getFilterItem(filterModel);

        // filter must be allowed
        if (!filterItem) {
            return false;
        }

        // if filter requires value, value must be set
        const filterExpectedParametersAmount =
            getFilterOperator(filterItem.type, filterModel.comparisonOperator)?.expectedParameterAmount ?? 0;
        if (filterExpectedParametersAmount > filterModel.values.length) {
            return false;
        }

        // range filter first must be greater than second (or equal)
        if (filterModel.comparisonOperator === ComparisonOperatorsEnum.InRange && filterModel.values[0] > filterModel.values[1]) {
            return false;
        }

        return true;
    }

    private isFilterEmpty(filterModel: ColumnFilterModel): boolean {
        const filterItem = this.getFilterItem(filterModel);
        return !filterModel.column && !filterModel.comparisonOperator && !filterItem?.type && !filterModel.values[0];
    }

    /**
     * initially read query params for "simple" filters (set via direct query param instead of the filter param).
     * These then get converted to filters and removed from the query params. This allows for simple filters to be set via URL.
     * E.g. simple filter param `?Id=1234` gets converted to normal filter param `?filters=And|Id|=|[1234]`.
     */
    private async readSimpleQueryParamFilters(): Promise<void> {
        const queryParamsObject = this.activatedRoute.snapshot.queryParams;
        const queryParams = Object.entries(queryParamsObject);

        const queryParamsToRemove: DictionaryType<null> = {};
        for (const [columnName, param] of queryParams) {
            const filterItem = this.filterItems().find(f => f.columnName === columnName);

            if (!filterItem) {
                continue;
            }

            if (Array.isArray(param)) {
                param.forEach(p => this.setFilter(p, filterItem, true));
            } else {
                this.setFilter(param, filterItem);
            }

            // eslint-disable-next-line no-null/no-null
            queryParamsToRemove[columnName] = null;
        }

        if (Object.keys(queryParamsToRemove).length > 0) {
            // remove all query params that were used for initally setting filters
            await this.router.navigate([], {
                replaceUrl: true,
                queryParams: queryParamsToRemove,
                queryParamsHandling: 'merge'
            });

            this.apply();
        }
    }

    private setFilter(param: any, filterItem: FilterItemModel, isArray = false): void {
        const stringParam = param.toString();
        let [value, operator = '='] = stringParam.split('|');

        if (filterItem.type === FilterItemTypeEnum.Boolean) {
            value = undefined;
            operator = stringParam;
        }

        if (filterItem.type === FilterItemTypeEnum.Number) {
            value = Number(value);
            if (Number.isNaN(value)) {
                // invalid filter
                return;
            }
        }

        const columnFilter = new ColumnFilterModel({
            column: filterItem.columnName,
            values: [value],
            comparisonOperator: ComparisonOperatorsEnumExtensions.operatorToEnum(operator),
            operand: isArray ? OperandTypeEnum.Or : OperandTypeEnum.And
        });

        if (filterItem.isReadOnly) {
            this.addReadonlyFilter(columnFilter);
        } else {
            this.addFilter(columnFilter);
        }
    }
}
